import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AmdenLogo } from "@/components/AmdenLogo";
import { Zap, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can sign in now");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "This reset link is invalid or has expired");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-8">
          <AmdenLogo className="h-9" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 leading-tight">Workforce &<br />Job Costing</div>
        </div>
        {!token ? (
          <div data-testid="reset-invalid" className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-3 py-3">
            This reset link is missing its token. Please request a new link.
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-zinc-950">Password updated</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-6">Your password has been changed. Sign in with your new password.</p>
            <Button data-testid="reset-goto-login" onClick={() => nav("/login")} className="w-full h-12 bg-slate-900 text-white">Go to sign in</Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <Zap className="h-9 w-9 text-amber-500 mb-4" />
            <h2 className="font-heading text-2xl font-bold text-zinc-950">Choose a new password</h2>
            <p className="text-sm text-zinc-500 mb-6">Set a strong password for your Amden Pulse account.</p>
            {error && <div data-testid="reset-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
            <div className="space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 mt-1" placeholder="••••••••" />
              </div>
              <div>
                <Label htmlFor="cf">Confirm password</Label>
                <Input id="cf" data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="h-12 mt-1" placeholder="••••••••" />
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
